/**
 * ImpersonateDialog — choose the role to impersonate a campus or corporation as.
 *
 * One copy (docs/DUPLICATION_AUDIT.md, C9) of the dialog the Campuses, Campus profile,
 * Corporations and Corporate profile pages each carried with its own CSS; only the
 * subject line differed. The page keeps the session switch itself (its
 * handleConfirmImpersonate) — this is the presentation.
 *
 * Two changes from the copies: Escape cancels (as the Cancel button does), and the
 * backdrop no longer carries aria-hidden, which hid the whole dialog — role="dialog"
 * included — from screen readers.
 *
 * The navigation's "change impersonation role" dialog (NavigationV2) is a different,
 * narrower dialog on its own overlay and is not this one.
 *
 * @param {node} subject — the campus or corporation name
 * @param {node} [detail] — muted, after an em dash: the corporation, or the campuses
 * @param {object[]} roles — { user_role_weight, user_role_name }, already filtered
 * @param {number|string} myRoleWeight — the "My Role" option's value
 * @param {number|string} roleWeight — the selected value
 * @param {(value: string) => void} onRoleChange
 * @param {boolean} busy — the switch is in progress ("Switching...")
 * @param {() => void} onCancel
 * @param {() => void} onConfirm
 */

import { useEffect, useId } from 'react';
import './impersonate-dialog.css';

const ImpersonateDialog = ({
    subject, detail, roles, myRoleWeight, roleWeight, onRoleChange, busy, onCancel, onConfirm,
}) => {
    const selectId = useId();

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onCancel]);

    return (
        <div className="dp-drawer-backdrop" onClick={onCancel}>
            <div className="impersonate-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Impersonate">
                <h3 className="impersonate-dialog__title">Impersonate</h3>
                <p className="impersonate-dialog__subject">
                    {subject}
                    {detail ? (
                        <span className="impersonate-dialog__detail"> — {detail}</span>
                    ) : null}
                </p>
                <div className="dp-drawer__field">
                    <label htmlFor={selectId} className="dp-drawer__label">
                        Impersonating User Role
                    </label>
                    <select
                        id={selectId}
                        value={roleWeight}
                        onChange={(e) => onRoleChange(e.target.value)}
                        className="dp-drawer__select"
                    >
                        <option value={myRoleWeight}>My Role</option>
                        {(roles || []).map((r) => (
                            <option key={r.user_role_weight} value={r.user_role_weight}>
                                {r.user_role_name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="impersonate-dialog__actions">
                    <button type="button" onClick={onCancel} className="dp-btn dp-btn--secondary">
                        Cancel
                    </button>
                    <button type="button" onClick={onConfirm} disabled={busy} className="dp-btn dp-btn--primary">
                        {busy ? 'Switching...' : 'Impersonate'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImpersonateDialog;
